// @flow
import * as React from 'react'
import omitEmptyFields from 'mz-utils/omitEmptyFields'
import { FormattedMessage } from 'mz-intl'
import getFlights from 'mz-sdk/services/flights/getFlights'
import type { Flight } from 'mz-sdk/services/flights/getFlights'
import AdvancedSearchTip from '../AdvancedSearchTip'
import SelectField from '../../SelectField'
import getFlightData from '../utils/getFlightData'
import flightToString from '../utils/flightToString'
import parseFlightQuery from '../utils/parseFlightQuery'
import messages from '../messages.intl'

// Types
import type {
  SelectInnerRefType,
  Props as SelectFieldProps,
} from '../../SelectField'

export type FlightInfo = {
  returnVoyage?: boolean,
  airlineCode?: string,
  airportCode: string,
  flightDirection: 'arr' | 'dep',
  pickupDatetime?: ?string,
  flightDatetime?: ?string,
}
export type Props = SelectFieldProps & {
  onOpenFinder?: () => any,
  flightInfo: FlightInfo,
  compactFlightDisplay?: boolean,
}

/**
 * Field for selecting flight around data provided in the `flightInfo` prop.
 * For examples take a look to attached stories.
 */
class BasicFlightField extends React.Component<Props> {
  static defaultProps = {
    ...SelectField.defaultProps,
    label: <FormattedMessage message={messages.FIELD_LABEL} />,
    placeholder: (
      <FormattedMessage
        message={messages.FIELD_PLACEHOLER}
        values={{ airlineCode: 'UA' }}
      />
    ),
  }

  selectInstance = null

  setSelectRef = (node: ?SelectInnerRefType) => {
    this.props.innerRef && this.props.innerRef(node)
    this.selectInstance = node
  }

  handleSelectUnknownValue = (value: any) => {
    const {
      input: { onChange },
    } = this.props
    onChange && onChange(value)
    this.selectInstance && this.selectInstance.hideOptions()
  }

  requestFlights = (query: string): Promise<Array<any>> => {
    const {
      airportCode,
      flightDirection,
      pickupDatetime,
      flightDatetime,
    } = this.props.flightInfo
    const { airlineCode, flightNumber } = getFlightData(
      query,
      this.props.flightInfo.airlineCode || ''
    )
    const parsedFlight = parseFlightQuery(query)

    if ((!parsedFlight || query.length < 2) && !airlineCode) {
      return Promise.resolve([])
    }

    const flightRequest = {
      airline_code: airlineCode,
      airport_code: airportCode,
      flight_direction: flightDirection,
      flight_number: flightNumber || undefined,
      flight_datetime: undefined,
      pickup_datetime: undefined,
    }

    if (flightDatetime) {
      flightRequest.flight_datetime = flightDatetime
    } else if (pickupDatetime) {
      flightRequest.pickup_datetime = pickupDatetime
    }

    return getFlights(omitEmptyFields(flightRequest))
      .then((flights) => this.enhanceFlightsResult(flights, query))
      .catch(() => [])
  }

  enhanceFlightsResult(flights: Flight[], query: string) {
    if (!flights.length) return flights
    return [
      ...flights,
      <AdvancedSearchTip
        key="tip"
        query={query}
        onSelectFlight={this.handleSelectUnknownValue}
        onOpenFinder={this.props.onOpenFinder}
        flightInfo={this.props.flightInfo}
      />,
    ]
  }

  renderValue = (flight: Flight) => {
    return flightToString(flight, { compact: this.props.compactFlightDisplay })
  }

  renderOption = (flight: Flight) => {
    return flightToString(flight)
  }

  renderEmpty = (query: ?string) =>
    query && (
      <AdvancedSearchTip
        query={query}
        onSelectFlight={this.handleSelectUnknownValue}
        onOpenFinder={this.props.onOpenFinder}
        flightInfo={this.props.flightInfo}
        emptyResult
      />
    )

  render() {
    return (
      <SelectField
        {...this.props}
        innerRef={this.setSelectRef}
        emptyValue={null}
        renderValue={this.renderValue}
        renderOption={this.renderOption}
        searchableOptions={{
          ...this.props.searchableOptions,
          requestOptionsOnMount: true,
          requestOptions: this.requestFlights,
          renderEmpty: this.renderEmpty,
        }}
      />
    )
  }
}

export default BasicFlightField
